/*
 * Copyright (c) University of Technology Sydney. All rights reserved.
 * See LICENSE file in the project root for full license information.
 *
 * Project Name: wpt-frontend.
 * Author: Tim Porritt
 * Created: 30/03/22
 */

import { LoadingSpinner } from '@/components';
import { FormsContext } from '@/components/contexts/FormsContext/FormsProvider';
import { SimpleAlert } from '@/components/SimpleAlert/SimpleAlert';
import { useAsyncLayoutEffect } from '@/utils/hooks/useAsyncEffect';
import { isNullish } from '@/wpt-lib/utils/isNullish';
import { Flex } from '@chakra-ui/react';
import { JsonSchema, UISchemaElement } from '@jsonforms/core';
import React, { ReactNode, useContext } from 'react';

const FormSchemas = ({
    id,
    render,
}: {
    id?: number;
    render: (schemas: { schema: JsonSchema; uischema: UISchemaElement }) => ReactNode;
}) => {
    const { getFormById, loadFormById, loading, error } = useContext(FormsContext);

    useAsyncLayoutEffect(async () => {
        if (!isNullish(id) && !getFormById(id)) {
            await loadFormById(id);
        }
    }, [id, getFormById, loadFormById]);

    const formSchemas = !isNullish(id) ? getFormById(id) : undefined;
    if (loading) {
        return <LoadingSpinner fullScreen />;
    }

    if (error) {
        return (
            <Flex justifyContent="center" mt={6}>
                <SimpleAlert variant="top-accent">Could not load form, please try again later.</SimpleAlert>
            </Flex>
        );
    }

    return (
        <>
            {formSchemas &&
                render({
                    schema: JSON.parse(formSchemas.schema),
                    uischema: JSON.parse(formSchemas.uischema),
                })}
        </>
    );
};

export default FormSchemas;
